import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Linkedin, Youtube, Facebook, Mail, MapPin, Phone, ArrowRight } from "lucide-react";
import { Logo } from "./Logo";

const FOOTER_NAV = [
  {
    title: "Products",
    items: [
      { to: "/products/category/prefab-homes", label: "Prefab Homes" },
      { to: "/products/category/precast-villas", label: "Precast Villas" },
      { to: "/products/category/space-capsules", label: "Space Capsules" },
      { to: "/products/category/apple-pods", label: "Apple Pods" },
      { to: "/products/category/commercial-pods", label: "Commercial Pods" },
      { to: "/products/category/hospitality-resort", label: "Hospitality & Resort" },
    ],
  },
  {
    title: "Solutions",
    items: [
      { to: "/products?segment=B2C", label: "Residential" },
      { to: "/products/category/commercial-pods", label: "Commercial" },
      { to: "/hospitality", label: "Hospitality" },
      { to: "/products/category/commercial-pods", label: "Workspaces" },
      { to: "/products/category/commercial-pods", label: "Retail Spaces" },
    ],
  },
  {
    title: "Company",
    items: [
      { to: "/about", label: "About Us" },
      { to: "/about#process", label: "Our Process" },
      { to: "/projects", label: "Projects" },
      { to: "/sustainability", label: "Sustainability" },
      { to: "/contact", label: "Careers" },
    ],
  },
  {
    title: "Resources",
    items: [
      { to: "/resources", label: "Brochure" },
      { to: "/resources#catalogues", label: "Catalogues" },
      { to: "/projects", label: "Case Studies" },
      { to: "/resources#faqs", label: "FAQs" },
    ],
  },
];

const SDG_GOALS = [
  { n: "08", name: "Decent Work and Economic Growth", img: "/images/sdg/goal-8.jpg", href: "https://sdgs.un.org/goals/goal8" },
  { n: "09", name: "Industry, Innovation and Infrastructure", img: "/images/sdg/goal-9.jpg", href: "https://sdgs.un.org/goals/goal9" },
  { n: "12", name: "Responsible Consumption and Production", img: "/images/sdg/goal-12.jpg", href: "https://sdgs.un.org/goals/goal12" },
];

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-[#070605] text-[#F2EDE3] relative overflow-hidden border-t border-[#D9A441]/15">
      <div className="relative max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14 pt-16 md:pt-20 pb-10">
        {/* Main columns */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-12 lg:grid-cols-[minmax(0,3fr)_repeat(4,minmax(0,2fr))_minmax(0,3.4fr)]">
          {/* Brand */}
          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <div className="mb-5">
              <Logo inverted height={38} />
            </div>
            <p className="text-[#F2EDE3]/55 text-sm leading-relaxed max-w-xs">
              We build smart, sustainable, and stunning spaces through modular
              innovation and precision engineering.
            </p>
            <div className="mt-6 flex items-center gap-3">
              {[
                { Icon: Facebook, label: "Facebook", href: "https://www.facebook.com/people/Nuvun-Homes/61569291323365/" },
                { Icon: Instagram, label: "Instagram", href: "https://www.instagram.com/nuvunhomes/" },
                { Icon: Linkedin, label: "LinkedIn", href: "https://www.linkedin.com/company/nuvun-homes/" },
                { Icon: Youtube, label: "YouTube", href: "https://www.youtube.com/@NuvunHomes" },
              ].map(({ Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="w-9 h-9 rounded-full border border-[#D9A441]/30 flex items-center justify-center text-[#F2EDE3]/60 hover:text-[#0A0908] hover:bg-[#D9A441] hover:border-[#D9A441] transition-all duration-300"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Nav columns */}
          {FOOTER_NAV.map((col) => (
            <div key={col.title} className="lg:col-span-1">
              <div className="eyebrow text-[#D9A441] mb-5">{col.title}</div>
              <ul className="space-y-2.5">
                {col.items.map((it, i) => (
                  <li key={`${it.label}-${i}`}>
                    <Link
                      to={it.to}
                      data-testid={`footer-link-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="font-body text-sm text-[#F2EDE3]/60 hover:text-[#D9A441] transition-colors"
                    >
                      {it.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Get in touch */}
          <div className="col-span-2 md:col-span-3 lg:col-span-1 lg:min-w-0">
            <div className="eyebrow text-[#D9A441] mb-5">Get In Touch</div>
            <div className="space-y-3.5 text-sm text-[#F2EDE3]/60">
              <a href="tel:+919696960998" data-testid="footer-phone" className="flex items-center gap-2.5 hover:text-[#D9A441] transition-colors">
                <Phone className="w-3.5 h-3.5 text-[#D9A441] shrink-0" /> +91 96969 60998
              </a>
              <a href="mailto:business@nuvunhomes.com" data-testid="footer-email" className="flex items-center gap-2.5 hover:text-[#D9A441] transition-colors break-all">
                <Mail className="w-3.5 h-3.5 text-[#D9A441] shrink-0" /> business@nuvunhomes.com
              </a>
              <a href="mailto:info@nuvunhomes.com" data-testid="footer-email-2" className="flex items-center gap-2.5 hover:text-[#D9A441] transition-colors break-all">
                <Mail className="w-3.5 h-3.5 text-[#D9A441] shrink-0" /> info@nuvunhomes.com
              </a>
              <div className="flex items-start gap-2.5 leading-relaxed">
                <MapPin className="w-3.5 h-3.5 text-[#D9A441] shrink-0 mt-0.5" />
                <span className="break-words">Brillen Space Engineering, 5th Floor, Eashan Arcade, Doctor&apos;s Colony, Madhapur, Hyderabad, Telangana 500081</span>
              </div>
            </div>
            <Link to="/contact" data-testid="footer-cta-enquire" className="btn-gold mt-6 !px-5 !py-3 whitespace-nowrap">
              Start Your Project <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>

        {/* SDG alignment */}
        <div className="relative mt-14 pt-10 border-t border-[#D9A441]/12" data-testid="footer-sdg">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
            <div className="lg:col-span-4">
              <div className="eyebrow text-[#D9A441] mb-3">UN Sustainable Development Goals</div>
              <h3 className="font-display text-[#F2EDE3] text-2xl md:text-3xl leading-[1.15]">
                We are aligned <span className="text-[#F2EDE3]/45">to the</span> <span className="text-[#D9A441] italic">goals.</span>
              </h3>
              <p className="font-body text-sm text-[#F2EDE3]/50 mt-4 max-w-md leading-relaxed">
                Every NUVUN build advances responsible production, modern infrastructure
                and dignified livelihoods — aligned to the UN&apos;s 2030 Agenda.
              </p>
              <a
                href="https://sdgs.un.org/goals"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-sdg-logo-link"
                className="mt-6 inline-flex items-center gap-3 group"
              >
                <img
                  src="/images/sdg/sdg-logo.png"
                  alt="UN Sustainable Development Goals"
                  className="h-12 md:h-14 w-auto opacity-95 group-hover:opacity-100 transition-opacity"
                />
              </a>
            </div>
            <div className="lg:col-span-4">
              <div className="grid grid-cols-3 gap-3 md:gap-4 max-w-[360px] md:max-w-[380px]">
                {SDG_GOALS.map((g) => (
                  <a
                    key={g.n}
                    href={g.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`footer-sdg-${parseInt(g.n, 10)}`}
                    aria-label={`SDG ${parseInt(g.n, 10)} — ${g.name}`}
                    className="group block relative overflow-hidden aspect-square rounded-md border border-white/10 hover:border-[#D9A441]/60 transition-all duration-500"
                  >
                    <img
                      src={g.img}
                      alt={`SDG ${parseInt(g.n, 10)} — ${g.name}`}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                      loading="lazy"
                    />
                  </a>
                ))}
              </div>
            </div>

            {/* EBG Group parent panel */}
            <div className="lg:col-span-4">
              <a
                href="https://theebg.com/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-ebg-panel"
                aria-label="An EBG Group Company — visit theebg.com"
                className="group block relative overflow-hidden rounded-md border-2 border-[#D9A441]/50 bg-gradient-to-br from-[#161311] via-[#0F0D0B] to-[#0A0908] px-6 py-6 md:px-7 md:py-7 hover:border-[#D9A441] hover:shadow-[0_0_28px_-6px_rgba(217,164,65,0.45)] transition-all duration-500"
              >
                <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_top_right,_rgba(217,164,65,0.14),_transparent_65%)]" />
                <div className="relative">
                  <div className="flex items-center gap-4">
                    <img
                      src="/images/brand/ebg-logo.png"
                      alt="EBG Group"
                      loading="lazy"
                      className="h-12 md:h-14 w-auto shrink-0"
                    />
                    <div className="min-w-0">
                      <div className="font-display text-lg md:text-xl text-[#F2EDE3] leading-tight">
                        An <span className="text-[#D9A441]">EBG</span> Group Company
                      </div>
                      <div className="text-xs text-[#F2EDE3]/50 mt-1 group-hover:text-[#D9A441]/80 transition-colors">
                        theebg.com <span aria-hidden>↗</span>
                      </div>
                    </div>
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="relative mt-12 pt-6 border-t border-[#D9A441]/12 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-[#F2EDE3]/40">
          <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
            <span>© {new Date().getFullYear()} NUVUN Modular Architecture. All rights reserved.</span>
          </div>
          <div className="flex flex-wrap items-center gap-5">
            <a
              href="https://www.reachvel.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-reachvel-credit"
              className="hover:text-[#D9A441] transition-colors"
            >
              Design &amp; Development by <span className="text-[#F2EDE3]/70">Reachvel</span>
            </a>
            <span className="hidden md:inline w-px h-3 bg-white/15" />
            <Link to="/contact" className="hover:text-[#D9A441] transition-colors" data-testid="footer-privacy">Privacy Policy</Link>
            <Link to="/contact" className="hover:text-[#D9A441] transition-colors" data-testid="footer-terms">Terms of Use</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
